import {
  getStripRetimePointsLocal,
  getStripRetimePointsNormalized,
  isStripRetimeEnabled,
  migrateStripRetimePointsToNormalized,
  setStripRetimePointsNormalized,
} from '@/editor/timing/retimePoints'
import { mapLocalToMedia, speedAtLocal } from '@/editor/timing/retime'

const MIN_STRIP_LENGTH = 1e-3
const MIN_AVG_SPEED = 1e-6
const MIN_RETIME_SPEED = 0.01
const MAX_RETIME_SPEED = 100

const clamp = (value, min, max) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return min
  if (n <= min) return min
  if (n >= max) return max
  return n
}

const finitePositive = (value, fallback) => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

const finiteNonNegative = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

const normalizeAnchor = (anchor) => (anchor === 'front' ? 'front' : 'back')
const normalizeMode = (mode) => (mode === 'step' ? 'step' : 'linear')
const normalizeAffects = (affects) => (affects === 'right' ? 'right' : 'left')
const normalizeColor = (color) => {
  const raw = `${color || ''}`.trim()
  return /^#[0-9a-f]{6}$/i.test(raw) ? raw.toLowerCase() : null
}

const clampSpeed = (value, fallback = 1) => {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return Math.max(MIN_RETIME_SPEED, Math.min(MAX_RETIME_SPEED, n))
}

function boundaryAffects(edge, mode = 'step') {
  const normalizedMode = normalizeMode(mode)
  if (normalizedMode === 'step') {
    return edge === 'start' ? 'right' : 'left'
  }
  return 'left'
}

function deriveBaselineSpeed(strip, clipLen) {
  const len = Math.max(MIN_STRIP_LENGTH, finitePositive(clipLen, MIN_STRIP_LENGTH))
  const legacyScale = finitePositive(strip?.timeScale, 1)
  const coverage = finitePositive(strip?.sourceDuration, len * legacyScale)
  if (!finitePositive(strip?.sourceDuration, NaN)) {
    strip.sourceDuration = coverage
  }
  return clampSpeed(coverage / len, legacyScale)
}

function sampleBoundarySpeed(edge, points, clipLen, mode, fallback) {
  if (!Array.isArray(points) || !points.length) return fallback
  const len = Math.max(MIN_STRIP_LENGTH, finitePositive(clipLen, MIN_STRIP_LENGTH))
  const eps = Math.min(1e-4, Math.max(1e-6, len / 1000))
  const sampleT = edge === 'start'
    ? Math.min(len, eps)
    : Math.max(0, len - eps)
  const speed = speedAtLocal(sampleT, len, points, normalizeMode(mode))
  return clampSpeed(speed, fallback)
}

function ensureExplicitBoundaryPoints(strip, clipLen = null) {
  if (!strip || !isStripRetimeEnabled(strip)) return []
  const len = Math.max(
    MIN_STRIP_LENGTH,
    finitePositive(clipLen, finitePositive(strip.length, MIN_STRIP_LENGTH))
  )
  const mode = normalizeMode(strip?.retime?.mode)
  strip.retime.mode = mode
  const baselineSpeed = deriveBaselineSpeed(strip, len)
  const points = getStripRetimePointsLocal(strip, len)
  const boundaryTolerance = Math.max(1e-6, len * 1e-6)
  const interior = []
  let startPoint = null
  let endPoint = null

  points.forEach((point) => {
    const t = clamp(Number(point?.t) || 0, 0, len)
    const normalizedPoint = {
      t,
      speed: clampSpeed(point?.speed, baselineSpeed),
      affects: normalizeAffects(point?.affects),
    }
    if (Math.abs(t) <= boundaryTolerance) {
      if (!startPoint) startPoint = normalizedPoint
      return
    }
    if (Math.abs(t - len) <= boundaryTolerance) {
      if (!endPoint) endPoint = normalizedPoint
      return
    }
    interior.push(normalizedPoint)
  })

  const localized = points.length ? points : interior
  const startBoundary = {
    t: 0,
    speed: startPoint
      ? clampSpeed(startPoint.speed, baselineSpeed)
      : sampleBoundarySpeed('start', localized, len, mode, baselineSpeed),
    affects: boundaryAffects('start', mode),
  }
  const endBoundary = {
    t: len,
    speed: endPoint
      ? clampSpeed(endPoint.speed, baselineSpeed)
      : sampleBoundarySpeed('end', localized, len, mode, baselineSpeed),
    affects: boundaryAffects('end', mode),
  }

  const nextPoints = [startBoundary, ...interior, endBoundary]
    .sort((a, b) => a.t - b.t)

  setStripRetimePointsNormalized(strip, nextPoints, len)
  return nextPoints
}

function scaleKeyframesForLength(strip, oldLen, newLen) {
  const prev = finitePositive(oldLen, MIN_STRIP_LENGTH)
  const next = finitePositive(newLen, MIN_STRIP_LENGTH)
  const keyScale = prev > 0 ? (next / prev) : 1
  if (!strip?.animation || !Array.isArray(strip.animation.keyframes)) return
  if (!Number.isFinite(keyScale) || keyScale <= 0 || keyScale === 1) return
  strip.animation.keyframes = strip.animation.keyframes
    .map((kf) => {
      const out = { ...kf }
      out.time = Math.max(0, Math.min(next, (Number(kf.time) || 0) * keyScale))
      const delay = (kf.delay && typeof kf.delay === 'object') ? kf.delay : {}
      out.delay = { ...(delay || {}) }
      Object.keys(out.delay).forEach((prop) => {
        const d = Number(out.delay[prop]) || 0
        out.delay[prop] = Math.max(0, d * keyScale)
      })
      return out
    })
    .sort((a, b) => (a.time || 0) - (b.time || 0))
}

function applyRetimeAutofit(strip, anchor = 'back') {
  if (!strip) return
  const oldLen = Math.max(MIN_STRIP_LENGTH, finitePositive(strip.length, MIN_STRIP_LENGTH))
  migrateStripRetimePointsToNormalized(strip, oldLen)
  if (isStripRetimeEnabled(strip)) {
    ensureExplicitBoundaryPoints(strip, oldLen)
  }
  const points = getStripRetimePointsNormalized(strip, oldLen)
  const mode = strip?.retime?.mode === 'step' ? 'step' : 'linear'

  const targetCoverage = finitePositive(
    strip.sourceDuration,
    finitePositive(strip.origDuration, oldLen)
  )
  if (!finitePositive(strip.sourceDuration, NaN)) {
    strip.sourceDuration = targetCoverage
  }

  // Phase 3: single duration derivation path from curve integration.
  const avgSpeedRaw = points.length
    ? mapLocalToMedia(1, 1, points, mode)
    : 1
  const avgSpeed = Math.max(MIN_AVG_SPEED, finitePositive(avgSpeedRaw, 1))
  const newLen = Math.max(MIN_STRIP_LENGTH, targetCoverage / avgSpeed)

  if (normalizeAnchor(anchor) === 'front') {
    strip.start = finiteNonNegative(
      (Number(strip.start) || 0) + (oldLen - newLen),
      finiteNonNegative(strip.start, 0)
    )
  }
  strip.length = newLen
  scaleKeyframesForLength(strip, oldLen, newLen)
}

export default {
  // Phase 1-2 (retime): enable/disable lane per strip
  toggleStripRetimeEnabled: (state, { tuuid, suuid, enabled, mode = null }) => {
    if (state._pushUndo) state._pushUndo('retime:toggle')
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip) return
    if (!strip.retime) strip.retime = { enabled: !!enabled, points: [], mode: 'step' }
    migrateStripRetimePointsToNormalized(strip, strip.length)
    strip.retime.enabled = !!enabled
    strip.retime.mode = mode === null
      ? (strip.retime.mode || 'step')
      : normalizeMode(mode)
    if (strip.retime.enabled) {
      ensureExplicitBoundaryPoints(strip, strip.length)
    }
  },
  // Add retime point (UI supplies local time in seconds; stored as normalized u)
  addRetimePoint: (state, { tuuid, suuid, t, speed = 1, affects = 'left' }) => {
    if (state._pushUndo) state._pushUndo('retime:add')
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip) return
    if (!strip.retime) strip.retime = { enabled: true, points: [] }
    strip.retime.enabled = true
    const clipLen = Math.max(0, Number(strip.length) || 0)
    const tt = clamp(Number(t) || 0, 0, clipLen)
    const sp = Number(speed)
    const ss = Number.isFinite(sp) && sp > 0 ? sp : 1
    const next = [...getStripRetimePointsNormalized(strip, clipLen), { t: tt, speed: ss, affects: normalizeAffects(affects) }]
    setStripRetimePointsNormalized(strip, next, clipLen)
    ensureExplicitBoundaryPoints(strip, clipLen)
  },
  addRetimePointLive: (state, { tuuid, suuid, t, speed = 1, affects = 'left' }) => {
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip) return
    if (!strip.retime) strip.retime = { enabled: true, points: [] }
    strip.retime.enabled = true
    const clipLen = Math.max(0, Number(strip.length) || 0)
    const tt = clamp(Number(t) || 0, 0, clipLen)
    const sp = Number(speed)
    const ss = Number.isFinite(sp) && sp > 0 ? sp : 1
    const next = [...getStripRetimePointsNormalized(strip, clipLen), { t: tt, speed: ss, affects: normalizeAffects(affects) }]
    setStripRetimePointsNormalized(strip, next, clipLen)
    ensureExplicitBoundaryPoints(strip, clipLen)
  },
  // Update retime point by index (optional t and/or speed; persisted as u/speed)
  updateRetimePoint: (state, { tuuid, suuid, index, t = null, speed = null, affects = null }) => {
    if (state._pushUndo) state._pushUndo('retime:update')
    const strip = state.tracks[tuuid]?.strips?.[suuid]
    if (!strip || !strip.retime) return
    const clipLen = Math.max(0, Number(strip.length) || 0)
    const pts = getStripRetimePointsNormalized(strip, clipLen)
    if (index < 0 || index >= pts.length) return
    const p = { ...pts[index] }
    if (t !== null) {
      const tt = clamp(Number(t) || 0, 0, clipLen)
      p.u = clipLen > 0 ? (tt / clipLen) : 0
    }
    if (speed !== null) {
      const sp = Number(speed)
      p.speed = Number.isFinite(sp) && sp > 0 ? sp : p.speed
    }
    if (affects !== null) p.affects = normalizeAffects(affects)
    pts[index] = p
    setStripRetimePointsNormalized(strip, pts, clipLen)
    ensureExplicitBoundaryPoints(strip, clipLen)
  },
  updateRetimePointLive: (state, { tuuid, suuid, index, t = null, speed = null, affects = null }) => {
    const strip = state.tracks[tuuid]?.strips?.[suuid]
    if (!strip || !strip.retime) return
    const clipLen = Math.max(0, Number(strip.length) || 0)
    const pts = getStripRetimePointsNormalized(strip, clipLen)
    if (index < 0 || index >= pts.length) return
    const p = { ...pts[index] }
    if (t !== null) {
      const tt = clamp(Number(t) || 0, 0, clipLen)
      p.u = clipLen > 0 ? (tt / clipLen) : 0
    }
    if (speed !== null) {
      const sp = Number(speed)
      p.speed = Number.isFinite(sp) && sp > 0 ? sp : p.speed
    }
    if (affects !== null) p.affects = normalizeAffects(affects)
    pts[index] = p
    setStripRetimePointsNormalized(strip, pts, clipLen)
    ensureExplicitBoundaryPoints(strip, clipLen)
  },
  // Remove retime point by index
  removeRetimePoint: (state, { tuuid, suuid, index }) => {
    if (state._pushUndo) state._pushUndo('retime:remove')
    const strip = state.tracks[tuuid]?.strips?.[suuid]
    if (!strip || !strip.retime) return
    const clipLen = Math.max(0, Number(strip.length) || 0)
    const pts = getStripRetimePointsNormalized(strip, clipLen)
    if (index < 0 || index >= pts.length) return
    pts.splice(index, 1)
    setStripRetimePointsNormalized(strip, pts, clipLen)
    ensureExplicitBoundaryPoints(strip, clipLen)
  },
  removeRetimePointLive: (state, { tuuid, suuid, index }) => {
    const strip = state.tracks[tuuid]?.strips?.[suuid]
    if (!strip || !strip.retime) return
    const clipLen = Math.max(0, Number(strip.length) || 0)
    const pts = getStripRetimePointsNormalized(strip, clipLen)
    if (index < 0 || index >= pts.length) return
    pts.splice(index, 1)
    setStripRetimePointsNormalized(strip, pts, clipLen)
    ensureExplicitBoundaryPoints(strip, clipLen)
  },
  setRetimePointsLocalLive: (state, { tuuid, suuid, points = [], clipLength = null }) => {
    const strip = state.tracks[tuuid]?.strips?.[suuid]
    if (!strip) return
    if (!strip.retime) strip.retime = { enabled: true, points: [] }
    strip.retime.enabled = true
    const len = Math.max(
      MIN_STRIP_LENGTH,
      finitePositive(clipLength, finitePositive(strip.length, MIN_STRIP_LENGTH))
    )
    setStripRetimePointsNormalized(strip, points, len)
    ensureExplicitBoundaryPoints(strip, len)
  },
  // Phase 3: recompute clip duration from integrated curve/source coverage.
  retimeAutofitLength: (state, { tuuid, suuid, anchor = 'back' }) => {
    if (state._pushUndo) state._pushUndo('retime:autofit')
    const strip = state.tracks[tuuid]?.strips?.[suuid]
    if (!strip) return
    applyRetimeAutofit(strip, normalizeAnchor(anchor))
  },
  retimeAutofitLengthLive: (state, { tuuid, suuid, anchor = 'back' }) => {
    const strip = state.tracks[tuuid]?.strips?.[suuid]
    if (!strip) return
    applyRetimeAutofit(strip, normalizeAnchor(anchor))
  },
  setRetimeAnchor: (state, { tuuid, suuid, anchor = 'back' }) => {
    if (state._pushUndo) state._pushUndo('retime:setAnchor')
    const strip = state.tracks[tuuid]?.strips?.[suuid]
    if (!strip) return
    if (!strip.retime) strip.retime = { enabled: true, points: [] }
    migrateStripRetimePointsToNormalized(strip, strip.length)
    strip.retime.anchor = normalizeAnchor(anchor)
    if (strip.retime.enabled) {
      ensureExplicitBoundaryPoints(strip, strip.length)
    }
  },
  setStripRetimeColor: (state, { tuuid, suuid, color }) => {
    if (state._pushUndo) state._pushUndo('retime:setColor')
    const strip = state.tracks[tuuid]?.strips?.[suuid]
    if (!strip) return
    const nextColor = normalizeColor(color)
    if (!nextColor) return
    if (!strip.retime) strip.retime = { enabled: false, points: [], mode: 'step' }
    strip.retime.color = nextColor
  },
}

